import { useSignupActions } from "../useSignupActions";
import { AuthNextStep } from "@sellar-io/common";
import { Button, H2 } from "@sellar-io/components";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useLocalStorage } from "usehooks-ts";
import { useEvents } from "~/hooks";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../hooks/useSignupContext.shared";
import { getJwtUrl } from "../signupUrl";
import { Icon } from "../../../../components";
import { Alert } from "~/components/alert/Alert";
import { twMerge } from "tailwind-merge";

export const UserAboutStep = ({
  setEmail,
  previousEmail,
  supplier,
}: {
  setEmail: (email: string) => void;
  previousEmail?: string;
  supplier?: {
    Business: {
      name: string;
      websiteUrl: string | null;
    };
  };
}) => {
  const { track } = useEvents();
  const [magicLinkEmail] = useLocalStorage("magicLinkEmail", "");
  const [searchParams] = useSearchParams();

  const [userType, setUserType] = useState<"business" | "consumer">();
  const [showConsumerMessage, setShowConsumerMessage] = useState(false);

  const { setStep } = useSignupContext();

  const businessUserInviteJwt =
    getJwtUrl(searchParams.get("continue")) ?? searchParams.get("jwt");
  const {
    loading,
    error,
    setError,
    runSubmission,
    getNextStep,
    continueSignIn,
    createCode,
    trackSignup,
  } = useSignupActions({ setEmail, previousEmail, businessUserInviteJwt });
  useEffect(() => {
    track("Enter Email Page Viewed");
  }, [track]);

  const onSubmit = async () => {
    if (loading || !userType) return;
    setError(undefined);
    if (userType === "consumer") {
      setShowConsumerMessage(true);
      return;
    }

    await runSubmission(async (isCurrent) => {
      // Recheck because account state may have changed since email entry.
      const { nextStep } = await getNextStep({
        email: magicLinkEmail,
      });
      if (!isCurrent()) return;
      if (nextStep === AuthNextStep.SignIn)
        return continueSignIn(magicLinkEmail);
      await createCode(magicLinkEmail);
      if (!isCurrent()) return;
      setEmail(magicLinkEmail);
      setStep(SignupFlowStep.MagicLinkSendStep);
      trackSignup(magicLinkEmail);
    });
  };

  const windowOpen = (url: string | null) => {
    if (!url) return;
    if (!url.match(/^https?:\/\//i)) {
      url = "http://" + url;
    }
    return window.open(url);
  };

  return (
    <>
      <div
        className={"m-auto flex h-full w-full flex-col justify-center gap-4"}
      >
        {showConsumerMessage ? (
          <div className={"flex flex-col items-center gap-2 text-center"}>
            {supplier ? (
              <>
                <H2>
                  Shop for {supplier.Business.name}'s products at their online
                  store
                </H2>
                <p>
                  Sellar is built for <b>businesses</b> to purchase craft
                  products directly from producers. As a private individual, you
                  can purchase {supplier.Business.name}'s products from by
                  following the link below.
                </p>
                {!!supplier.Business.websiteUrl && (
                  <Button
                    className="mt-4"
                    onClick={() => windowOpen(supplier.Business.websiteUrl)}
                  >
                    Go to {supplier.Business.name}'s store{" "}
                    <Icon icon="CHEVRON_RIGHT" />
                  </Button>
                )}
              </>
            ) : (
              <>
                <H2>
                  Shop for craft products at your favourite producers' online
                  stores
                </H2>
                <p>
                  Sellar is built for <b>businesses</b> to purchase craft
                  products directly from producers. As a private individual, you
                  can purchase craft products from your favourite producers by
                  visiting their websites.
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className={"flex flex-col gap-2 text-center"}>
              <H2>Tell us a bit about yourself</H2>
              <p>What brings you here today?</p>
            </div>
            {error && (
              <Alert
                type={"error"}
                title={error.title}
                message={error.description}
                className={"w-full"}
              />
            )}
            <form
              className={"flex flex-col gap-2"}
              onSubmit={(event) => {
                event.preventDefault();
                void onSubmit();
              }}
            >
              <UserTypeSelector
                text="I'm buying for a business"
                icon="STOREFRONT"
                active={userType === "business"}
                setActive={() => setUserType("business")}
              />
              <UserTypeSelector
                text="I'm buying for myself"
                icon="PROFILE"
                active={userType === "consumer"}
                setActive={() => setUserType("consumer")}
              />
              <Button
                type={"submit"}
                className={"mt-4 w-full"}
                disabled={!userType || loading}
                loading={loading}
              >
                <span>Continue</span>
              </Button>
            </form>
          </>
        )}
      </div>
    </>
  );
};

const UserTypeSelector = ({
  text,
  icon,
  active,
  setActive,
}: {
  text: string;
  icon: "PROFILE" | "STOREFRONT";
  active: boolean;
  setActive: () => void;
}) => {
  return (
    <div
      className={twMerge(
        "flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-300 px-4 py-8",
        active
          ? "border-zinc-700 bg-zinc-200 hover:bg-zinc-100"
          : "hover:bg-zinc-50",
      )}
      onClick={() => setActive()}
    >
      <Icon icon={active ? "CHECKBOX_MARKED_CIRCLE" : icon} size={1} />
      {text}
    </div>
  );
};
